import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import DropdownSelect from "../../../../../../../components-new/form/DropdownSelect";
import BotAvatar from "../../../clients/new/overview/BotAvatar";
import {getBotStatisticNumbers} from '../../../../../../../services/statistics/api';
import LoadingComponent from "../../../../../../../components-new/ui/LoadingComponent";
import ExcelButton from "../../../../../../../components-new/buttons/ExcelButton";
import ResetButton from "../../../../../../../components-new/buttons/ResetButton";
import TableHeaderSort from "../../../../../../../components-new/table/TableHeaderSort";
import {globalExportToExcel} from "../../../../../../../utils/General";
import Translate, {t} from "../../../../../../../components/Translate";
import Colors from "../../../../../../../utils/Colors";
import {handleSort} from "../../../../../../../utils/TableUtils";
import DatePicker from "../../../../../../../components-new/form/DatePicker";
import TableScroll from "../../../../../../../components-new/table/TableScroll";

const BotPlatformRequestTable = ({
                                     company,
                                     onCellClick,
                                     user
                                 }) => {

    const subs = company.subs ? company.subs : [];

    const [filters, setFilters] = useState({
        companyIds: subs.map((sub) => sub.id),
        start: null,
        end: null
    })
    const [pageable, setPageable] = useState({sort: ''});
    const [bots, setBots] = useState([]);
    const [isFetching, setIsFetching] = useState(false);

    const sortedMessengers = [...company.tariff.messengers].sort((a, b) => {
        if (a.toLowerCase() === "webchat") return -1;
        if (b.toLowerCase() === "webchat") return 1;
        return 0;
    });

    const botOptions = useMemo(
        () => subs.map(sub => ({value: sub.id, label: sub.name, bot: sub})),
        [subs]
    );

    const selectedBotOptions = useMemo(
        () => botOptions.filter(option => filters.companyIds.includes(option.value)),
        [botOptions, filters.companyIds]
    );

    const allBotsSelected = botOptions.length > 0
        && selectedBotOptions.length === botOptions.length;

    const toggleBot = (id) => {
        updateFilter('companyIds', filters.companyIds.includes(id)
            ? filters.companyIds.filter(x => x !== id)
            : filters.companyIds.concat([id]));
    };

    const toggleAllBots = () => {
        updateFilter('companyIds', allBotsSelected ? [] : botOptions.map(o => o.value));
    };

    useEffect(() => {
        fetchData();
    }, [pageable.sort, filters]);

    function updateFilter(key, val) {
        setFilters({
            ...filters,
            [key]: val
        })
    }

    function fetchData() {
        setIsFetching(true);

        getBotStatisticNumbers(filters, pageable).then((res) => {
            if (res) {
                setBots(res);
            } else {
                setBots([]);
            }
        }).catch(e => {
            setBots([])
        }).finally(() => {
            setIsFetching(false);
        })
    }

    function calcCellWidth() {
        return 100 / (company.tariff.messengers.length + 1) + '%'
    }

    function resetFilters() {
        setFilters({
            ...filters,
            start: null,
            end: null
        })
    }

    const exportToExcel = () => {
        const columns = [
            {label: 'Bot', value: 'bot'},
            ...sortedMessengers.map(messenger => ({
                label: messenger,
                value: messenger,
            })),
        ];

        const data = bots.map(botNumbers => {
            const row = {bot: botNumbers.name};

            sortedMessengers.forEach(messenger => {
                row[messenger] = botNumbers[messenger.toLowerCase()];
            });

            return row;
        });

        globalExportToExcel({
            data,
            columns,
            fileName: 'Bot-Platform requests (' + filters.start + ' ' + filters.end + ').xlsx'
        });
    };

    return (
        <React.Fragment>
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '10px',
                width: '100%',
                flexWrap: 'wrap'
            }}>
                <div style={{display: 'flex', gap: '10px', flexWrap: 'wrap'}}>
                    {/* the bots were fixed to "all of them" here before: the
                        filter object carried companyIds from the first render
                        and nothing on screen could change it */}
                    {botOptions.length > 1 && (
                        <DropdownSelect
                            selectedValue={selectedBotOptions}
                            options={botOptions}
                            onChange={(option) => toggleBot(option.value)}
                            onSelectAll={toggleAllBots}
                            renderLabel={(selected) => {
                                if (!selected || selected.length === 0) return <Translate text="notSelected"/>;
                                if (selected.length === botOptions.length) return <Translate text="allBots"/>;
                                if (selected.length === 1) return selected[0].label;
                                return selected.length + ' / ' + botOptions.length;
                            }}
                            searchBy={botOptions.length > 7
                                ? (option, search) => option.label.toLowerCase().includes(search.toLowerCase())
                                : null}
                            variant={'outline'}
                            styles={{width: '260px'}}
                        >
                            {option => (
                                <div style={{display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0}}>
                                    <BotAvatar client={option.bot} size={26} imgSize={24}/>
                                    <span style={{
                                        overflow: 'hidden',
                                        whiteSpace: 'nowrap',
                                        textOverflow: 'ellipsis'
                                    }}>{option.label}</span>
                                </div>
                            )}
                        </DropdownSelect>
                    )}

                    <DatePicker
                        inputContainerStyle={{width: '240px', height: '40px'}}
                        value={filters.start}
                        onChange={e => updateFilter('start', e)}
                        onReset={() => updateFilter('start', null)}
                        disabled={isFetching}
                        placeholder={'startDate'}
                        panelAlign={{horizontal: 'right', vertical: ''}}
                    />
                    <DatePicker
                        inputContainerStyle={{width: '240px', height: '40px'}}
                        value={filters.end}
                        onChange={e => updateFilter('end', e)}
                        onReset={() => updateFilter('end', null)}
                        endOfDay
                        disabled={isFetching}
                        placeholder={'endDate'}
                    />

                    {(
                        (filters.start !== null || filters.end !== null) &&
                        <ResetButton
                            onClick={resetFilters}
                            width={150}
                            height={40}
                            variant="outline"
                        />
                    )}
                </div>
                <div>
                    <ExcelButton
                        onClick={exportToExcel}
                        width={190}
                        height={40}
                        variant="outline"
                        children={
                            <span style={{fontSize: '12px', backgroundColor: 'transparent'}}>
                                <Translate text={'downloadXls'}/>
                            </span>
                        }
                    />
                </div>
            </div>

            <br/>

            <TableScroll cellHover style={{maxHeight: '420px', width: '100%', minHeight: '200px'}}>
                <table className="custom-table">
                    <thead>
                    <tr>
                        <th style={{textAlign: 'left', width: calcCellWidth()}}
                            onClick={() => handleSort('name', pageable, setPageable)}>
                            <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                                <Translate text="bot"/>
                                <TableHeaderSort
                                    isActive={pageable.sort.split(',').slice(0, -1).join(',') === 'name'}
                                    direction={pageable.sort.split(',').pop()}
                                />
                            </div>
                        </th>
                        {sortedMessengers.map(val => (
                            <th key={val} style={{textAlign: 'center', width: calcCellWidth()}}
                                onClick={() => handleSort(val.toLowerCase(), pageable, setPageable)}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    justifyContent: 'center'
                                }}>
                                    {val.charAt(0).toUpperCase() + val.slice(1).toLowerCase()}
                                    <TableHeaderSort
                                        isActive={pageable.sort.split(',').slice(0, -1).join(',') === val.toLowerCase()}
                                        direction={pageable.sort.split(',').pop()}
                                    />
                                </div>
                            </th>
                        ))}
                    </tr>
                    </thead>
                    {
                        !isFetching &&
                        <tbody>
                        {
                            bots.map((bot) => (
                                <tr key={bot.id}>
                                    <td
                                        style={{
                                            textAlign: 'left',
                                            overflow: 'hidden',
                                            whiteSpace: 'nowrap',
                                            textOverflow: 'ellipsis'
                                    }}
                                    >{bot.name}</td>
                                    {sortedMessengers.map(messenger => (
                                        <td
                                            key={bot.id + messenger}
                                            className="clickable-td"
                                            style={{textAlign: 'center', cursor: 'pointer'}}
                                            onClick={() => onCellClick(messenger, {
                                                bots: [bot.id],
                                                platforms: [messenger],
                                                end: filters.end,
                                                start: filters.start
                                            })}
                                        >
                                            {bot[messenger.toLowerCase()]}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        }

                        {
                            bots.length === 0 && !isFetching &&
                            <tr className="table-no-result">
                                <td colSpan={sortedMessengers.length + 1}
                                    style={{textAlign: 'center', padding: '40px', color: Colors.silverWhiteSemiDark}}>
                                    <Translate text={'noResultsFound'}/>
                                </td>
                            </tr>
                        }
                        </tbody>
                    }

                </table>
                {
                    isFetching
                    &&
                    <div style={{height: '150px'}}>
                        <LoadingComponent/>
                    </div>
                }
            </TableScroll>
        </React.Fragment>
    );
};

BotPlatformRequestTable.propTypes = {
    company: PropTypes.object.isRequired,
    onCellClick: PropTypes.func.isRequired,
};

export default BotPlatformRequestTable;



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/management/statistics/components/requestsToOperators/BotPlatformRequestTable.jsx